import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLoaderData, useNavigation, useSearchParams, useSubmit } from "react-router-dom";

import CSV from "src/components/common/csv";
import Modal from "src/components/common/Modal";
import ServerFooter from "src/components/common/ServerFooter";
import { useAuthStore, User } from "src/components/main/authStore";
import {
    BillingUploaderEmailList,
    billingEmails,
    boolAndReducer,
    getDatefromWeekNum,
    getDatefromWeekNum0,
    MyDate,
    numberReducer,
    onlyUnique,
    weeknum,
} from "src/components/common/functions";
import Export from "./export";
import type {
    ImportedBillingItem,
    ImportedBillingLoaderData,
    ImportedBillingProject,
} from "./actions_loaders";

type CsvParser = {
    parse: (input: string | ArrayBuffer | null | undefined) => unknown[][];
};

type ImportedBillingQuery = {
    startWeek: string;
    endWeek: string;
    importWeek: string;
    projectManagerId: string;
    pipeline: string;
    subPipeline: string;
};

const thousandSeparator = (value: number | string, digits = 2) => {
    const numericValue = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numericValue)) return "";
    return numericValue.toLocaleString("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
};

const formatWeekLabel = (week: string) => week.replace(/^w/, "").replace("-", "");

function buildWeeks(): string[] {
    const dateIterator = new MyDate("2023/1/2");
    const dateArray: string[] = [];
    const currentWeekStart = getDatefromWeekNum0(weeknum(new MyDate()), 0);
    while (currentWeekStart && dateIterator < currentWeekStart) {
        dateArray.push(weeknum(dateIterator));
        dateIterator.setDate(dateIterator.getDate() + 7);
    }
    const current = weeknum(new MyDate());
    if (!dateArray.includes(current)) {
        dateArray.push(current);
    }
    return dateArray;
}

export default function ImportedBilling() {
    const {
        projects,
        rawData,
        fileExist,
        startWeek,
        endWeek,
        importWeek,
        projectManagerId,
        pipeline,
        subPipeline,
    } = useLoaderData() as ImportedBillingLoaderData;
    const [searchParams, setSearchParams] = useSearchParams();
    const submit = useSubmit();
    const navigation = useNavigation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const user = useAuthStore(state => state.user) as User;
    const isPM = String(user.projects) === "yes" && user.department !== "Executive";
    const [filterWords, setFilterWords] = useState("");
    const [invalid, setInvalid] = useState<string[]>([]);
    const [showDetail, setShowDetail] = useState(false);
    const weeks = useMemo(() => buildWeeks(), []);
    const projectsMap = useMemo(
        () => Object.fromEntries(projects.map(project => [project.id, project])),
        [projects]
    ) as Record<string, ImportedBillingProject>;

    const [query, setQuery] = useState<ImportedBillingQuery>({
        startWeek,
        endWeek,
        importWeek,
        projectManagerId,
        pipeline,
        subPipeline,
    });

    useEffect(() => {
        setQuery({
            startWeek,
            endWeek,
            importWeek,
            projectManagerId,
            pipeline,
            subPipeline,
        });
    }, [endWeek, importWeek, pipeline, projectManagerId, startWeek, subPipeline]);

    const realProjectIds = useMemo(
        () =>
            Object.keys(
                Object.entries(rawData)
                    .filter(([week]) => week >= startWeek && week <= endWeek)
                    .map(([, value]) => value)
                    .reduce<Record<string, ImportedBillingItem[]>>((acc, entry) => ({ ...acc, ...entry }), {})
            ),
        [endWeek, rawData, startWeek]
    );

    const filteredProjectIds = useMemo(
        () =>
            realProjectIds.filter(projectId => {
                const project = projectsMap[projectId];
                if (!project) return false;
                if (pipeline !== "All" && project.pipeline !== pipeline) return false;
                if (subPipeline !== "All" && project.subPipeline !== subPipeline) return false;
                if (
                    projectManagerId !== "All" &&
                    !((billingEmails as string[]).includes(user.email) || project.projectManagerId === projectManagerId)
                ) {
                    return false;
                }
                return filterWords
                    .toLowerCase()
                    .split(" ")
                    .filter(Boolean)
                    .map(word => JSON.stringify(project).toLowerCase().includes(word))
                    .reduce(boolAndReducer, true);
            }),
        [filterWords, pipeline, projectManagerId, projectsMap, realProjectIds, subPipeline, user.email]
    );

    const visibleWeeks = useMemo(
        () => weeks.filter(week => week >= startWeek && week <= endWeek),
        [endWeek, startWeek, weeks]
    );

    const page = Math.max(1, Number(searchParams.get("page") ?? 1) || 1);
    const limit = Math.max(1, Number(searchParams.get("limit") ?? 10) || 10);
    const pagedProjectIds = useMemo(() => {
        const startIndex = (page - 1) * limit;
        return filteredProjectIds.slice(startIndex, startIndex + limit);
    }, [filteredProjectIds, limit, page]);

    const projectManagerIds = filteredProjectIds
        .map(projectId => projectsMap[projectId]?.projectManagerId)
        .filter(Boolean)
        .filter(onlyUnique) as string[];

    const currentPipelineProjects = realProjectIds.filter(projectId =>
        isPM ? projectsMap[projectId]?.projectManagerId === user.id : true
    );

    const pipelineOptions = currentPipelineProjects
        .map(projectId => projectsMap[projectId]?.pipeline)
        .filter(Boolean)
        .filter(onlyUnique) as string[];

    const subPipelineOptions = currentPipelineProjects
        .map(projectId => projectsMap[projectId]?.subPipeline)
        .filter(Boolean)
        .filter(onlyUnique) as string[];

    const isLoading = navigation.state !== "idle";
    const canImport = (BillingUploaderEmailList as string[]).includes(user.email);

    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        if ((params.get("page") ?? "1") !== "1") {
            params.set("page", "1");
            setSearchParams(params, { replace: false });
        }
    }, [filterWords, searchParams, setSearchParams]);

    const updateSearchParams = (nextQuery: ImportedBillingQuery) => {
        const params = new URLSearchParams(searchParams);
        params.set("startWeek", nextQuery.startWeek);
        params.set("endWeek", nextQuery.endWeek);
        params.set("importWeek", nextQuery.importWeek);
        params.set("projectManagerId", nextQuery.projectManagerId);
        params.set("pipeline", nextQuery.pipeline);
        params.set("subPipeline", nextQuery.subPipeline);
        params.set("page", "1");
        params.set("limit", searchParams.get("limit") ?? "10");
        setSearchParams(params, { replace: false });
    };

    const loadDisabled =
        query.startWeek === startWeek &&
        query.endWeek === endWeek &&
        query.importWeek === importWeek &&
        query.projectManagerId === projectManagerId &&
        query.pipeline === pipeline &&
        query.subPipeline === subPipeline;

    const totalForProject = (projectId: string) =>
        visibleWeeks
            .map(week => rawData[week]?.[projectId]?.map(item => item.amount).reduce(numberReducer, 0) || 0)
            .reduce(numberReducer, 0);

    const handleImportFile = async (file: File) => {
        const reader = new FileReader();
        reader.onload = () => {
            const out = (CSV as CsvParser).parse(reader.result);
            if (!out) {
                window.alert("The file is not supported.");
                return;
            }

            const validRows = out.slice(2, -1);
            const map: Record<string, ImportedBillingItem[]> = {};
            const currentInvalid: string[] = [];

            for (const parsedRow of validRows) {
                const row = parsedRow.slice(2);
                const key = String(row[2] ?? "");
                if (!key) continue;
                const filteredKey = key.split(":")[1]?.split("_")[0] ?? "";
                const project = projects.find(item => item.projectNumber === filteredKey);
                if (!project) {
                    if (
                        !["PGE", "PG&E"].includes(filteredKey) &&
                        !["(PG&E_Gas Stand By)", "(PGE_USA Mark & Locate)", "(PGE_GPOM)", "(PGE_QEW Inspectors)"].includes(key)
                    ) {
                        currentInvalid.push(filteredKey);
                    }
                    continue;
                }

                const date = String(row[0] ?? "");
                const billingNumber = String(row[1] ?? "");
                const amount = Number(row[3] ?? 0);
                if (!amount || weeknum(date) !== query.importWeek) continue;

                if (!map[project.id]) {
                    map[project.id] = [];
                }
                map[project.id].push({ date, billingNumber, amount });
            }

            setInvalid(currentInvalid.filter(onlyUnique));
            const formData = new FormData();
            formData.append("intent", "import");
            formData.append("week", query.importWeek);
            formData.append("data", JSON.stringify(map));
            submit(formData, { method: "post", encType: "multipart/form-data" });
        };
        reader.readAsBinaryString(file);
    };

    return (
        <div className="cssContent pb8rem">
            {invalid.length ? (
                <Modal title="Invalid" touchOutsideClose closeIt={() => setInvalid([])}>
                    {invalid.map(item => (
                        <div key={item} className="user-select-text m-2">
                            {item}
                        </div>
                    ))}
                </Modal>
            ) : null}

            <nav className="my-3 d-flex align-items-center flex-wrap gap-2">
                <label htmlFor="importedBillingSearch" className="mb-0 fw-bold">
                    Search
                </label>
                <input
                    id="importedBillingSearch"
                    className="form-control inline w-auto"
                    type="text"
                    value={filterWords}
                    onChange={event => setFilterWords(event.currentTarget.value.toLowerCase())}
                />

                <label htmlFor="showDetail" className="mb-0">
                    Show Detail
                </label>
                <input
                    id="showDetail"
                    type="checkbox"
                    className="cssCheckBox"
                    checked={showDetail}
                    onChange={event => setShowDetail(event.currentTarget.checked)}
                />

                {!isPM ? (
                    <>
                        <label htmlFor="projectManagerId" className="mb-0">
                            Project Manager
                        </label>
                        <select
                            id="projectManagerId"
                            className="form-control inline w-auto"
                            value={query.projectManagerId}
                            onChange={event => setQuery(prev => ({ ...prev, projectManagerId: event.currentTarget.value }))}
                        >
                            <option value="All">All</option>
                            {projectManagerIds.map(managerId => (
                                <option key={managerId} value={managerId}>
                                    {projects.find(project => project.projectManagerId === managerId)?.projectManagerName ?? managerId}
                                </option>
                            ))}
                        </select>
                    </>
                ) : null}

                <label htmlFor="pipeline" className="mb-0">
                    Pipeline
                </label>
                <select
                    id="pipeline"
                    className="form-control inline w-auto"
                    value={query.pipeline}
                    onChange={event => setQuery(prev => ({ ...prev, pipeline: event.currentTarget.value }))}
                >
                    <option value="All">All</option>
                    {pipelineOptions.map(value => (
                        <option key={value} value={value}>
                            {value}
                        </option>
                    ))}
                </select>

                <label htmlFor="subPipeline" className="mb-0">
                    Sub Pipeline
                </label>
                <select
                    id="subPipeline"
                    className="form-control inline w-auto"
                    value={query.subPipeline}
                    onChange={event => setQuery(prev => ({ ...prev, subPipeline: event.currentTarget.value }))}
                >
                    <option value="All">All</option>
                    {subPipelineOptions.map(value => (
                        <option key={value} value={value}>
                            {value}
                        </option>
                    ))}
                </select>

                <label htmlFor="startWeek" className="mb-0">
                    Start Week
                </label>
                <select
                    id="startWeek"
                    className="form-control inline w-auto"
                    value={query.startWeek}
                    onChange={event => setQuery(prev => ({ ...prev, startWeek: event.currentTarget.value }))}
                >
                    {weeks
                        .filter(week => week <= query.endWeek)
                        .map(week => (
                            <option key={week} value={week}>
                                {formatWeekLabel(week)}
                            </option>
                        ))}
                </select>

                <label htmlFor="endWeek" className="mb-0">
                    End Week
                </label>
                <select
                    id="endWeek"
                    className="form-control inline w-auto"
                    value={query.endWeek}
                    onChange={event => setQuery(prev => ({ ...prev, endWeek: event.currentTarget.value }))}
                >
                    {weeks
                        .filter(week => week >= query.startWeek)
                        .map(week => (
                            <option key={week} value={week}>
                                {formatWeekLabel(week)}
                            </option>
                        ))}
                </select>

                <button type="button" className="btn btn-primary" onClick={() => updateSearchParams(query)} disabled={loadDisabled}>
                    Load
                </button>

                <Export
                    filteredProjectIds={filteredProjectIds}
                    visibleWeeks={visibleWeeks}
                    projectsMap={projectsMap}
                    rawData={rawData}
                />

                {canImport ? (
                    <>
                        <label htmlFor="importWeek" className="mb-0">
                            Week to Import
                        </label>
                        <select
                            id="importWeek"
                            className="form-control inline w-auto"
                            value={query.importWeek}
                            onChange={event => {
                                const nextQuery = { ...query, importWeek: event.currentTarget.value };
                                setQuery(nextQuery);
                                const params = new URLSearchParams(searchParams);
                                params.set("importWeek", nextQuery.importWeek);
                                setSearchParams(params, { replace: false });
                            }}
                        >
                            {weeks.map(week => (
                                <option key={week} value={week}>
                                    {formatWeekLabel(week)}
                                </option>
                            ))}
                        </select>

                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                                if (rawData[query.importWeek] && !window.confirm("Are you sure to overwrite the existing data of the week?")) {
                                    return;
                                }
                                fileInputRef.current?.click();
                            }}
                        >
                            Import
                        </button>
                        <input
                            ref={fileInputRef}
                            className="nodisplay"
                            type="file"
                            accept=".csv"
                            onChange={event => {
                                const file = event.currentTarget.files?.[0];
                                if (file) {
                                    void handleImportFile(file);
                                }
                                event.currentTarget.value = "";
                            }}
                        />

                        {fileExist ? (
                            <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() => {
                                    if (!window.confirm("Are you sure to delete the existing data of the week?")) return;
                                    const formData = new FormData();
                                    formData.append("intent", "delete");
                                    formData.append("week", query.importWeek);
                                    submit(formData, { method: "post" });
                                }}
                            >
                                Delete
                            </button>
                        ) : null}
                    </>
                ) : null}
            </nav>

            {isLoading ? (
                <div className="cssLoading">Loading...</div>
            ) : !filteredProjectIds.length ? (
                <div className="cssLoading">Empty</div>
            ) : (
                <>
                    {!isPM ? (
                        <div className="table-responsive px-2">
                            <table className="table">
                                <caption className="text-center fw-bold caption-top">
                                    Status Summary ({getDatefromWeekNum(startWeek, 0)} ~ {getDatefromWeekNum(endWeek, 6)})
                                </caption>
                                <tbody>
                                    <tr>
                                        <th>Project Manager</th>
                                        {projectManagerIds.map(managerId => (
                                            <th key={managerId}>{projects.find(project => project.projectManagerId === managerId)?.projectManagerName ?? managerId}</th>
                                        ))}
                                        <th>Total</th>
                                    </tr>
                                    <tr>
                                        <th>Number of Projects</th>
                                        {projectManagerIds.map(managerId => (
                                            <th key={managerId}>
                                                {realProjectIds.filter(projectId => projectsMap[projectId]?.projectManagerId === managerId).length}
                                            </th>
                                        ))}
                                        <th>{realProjectIds.length}</th>
                                    </tr>
                                    <tr>
                                        <th>Total Billing</th>
                                        {projectManagerIds.map(managerId => (
                                            <th key={managerId} className="text-success">
                                                {thousandSeparator(
                                                    filteredProjectIds
                                                        .filter(projectId => projectsMap[projectId]?.projectManagerId === managerId)
                                                        .map(projectId => totalForProject(projectId))
                                                        .reduce(numberReducer, 0)
                                                )}
                                            </th>
                                        ))}
                                        <th className="text-success">
                                            {thousandSeparator(filteredProjectIds.map(projectId => totalForProject(projectId)).reduce(numberReducer, 0))}
                                        </th>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    ) : null}

                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr className="stickyHeader">
                                    <th>Project Name</th>
                                    {visibleWeeks.map(week => (
                                        <th key={week}>{formatWeekLabel(week)}</th>
                                    ))}
                                    <th>Total Billing</th>
                                </tr>
                            </thead>
                            <tbody className="noTableTopBorder">
                                {pagedProjectIds.map(projectId => (
                                    <tr key={projectId}>
                                        <th>
                                            <Link to={`/Projects/${projectId}`}>{projectsMap[projectId]?.projectName ?? projectId}</Link>
                                        </th>
                                        {visibleWeeks.map(week => {
                                            const items = rawData[week]?.[projectId] ?? [];
                                            const total = items.map(item => item.amount).reduce(numberReducer, 0);
                                            return (
                                                <td key={week} className="text-center">
                                                    {total ? (
                                                        <>
                                                            {thousandSeparator(total)}
                                                            {showDetail ? (
                                                                <ul className="mt-2 text-start">
                                                                    {items.map(item => (
                                                                        <li key={`${item.date}-${item.billingNumber}-${item.amount}`}>
                                                                            <span className="text-secondary">{item.date}</span>{" "}
                                                                            <span className="text-info">#{item.billingNumber}</span>{" "}
                                                                            <span className="text-success">${item.amount}</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : null}
                                                        </>
                                                    ) : null}
                                                </td>
                                            );
                                        })}
                                        <td className="text-success text-center">{thousandSeparator(totalForProject(projectId))}</td>
                                    </tr>
                                ))}
                                <tr>
                                    <th>Total: {filteredProjectIds.length}</th>
                                    {visibleWeeks.map(week => {
                                        const total = filteredProjectIds
                                            .map(projectId => rawData[week]?.[projectId]?.map(item => item.amount).reduce(numberReducer, 0) || 0)
                                            .reduce(numberReducer, 0);
                                        return (
                                            <th key={week} className="text-center">
                                                {total ? thousandSeparator(total) : ""}
                                            </th>
                                        );
                                    })}
                                    <th className="text-success text-center">
                                        {thousandSeparator(filteredProjectIds.map(projectId => totalForProject(projectId)).reduce(numberReducer, 0))}
                                    </th>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <ServerFooter length={filteredProjectIds.length} pa="" />
                </>
            )}
        </div>
    );
}
